import React, { useEffect, useState } from 'react';
import { X, Plus, Trash2, Loader2 } from 'lucide-react';
import {
  listFields,
  createField,
  updateField,
  removeField,
  MarketingLeadField,
  MarketingLeadFieldType,
} from '@/services/marketingLeadService';
import { CuteConfirmModal } from '@/shared/components/CuteConfirmModal';

const FIELD_TYPES: { value: MarketingLeadFieldType; label: string }[] = [
  { value: 'TEXT', label: 'Texte' },
  { value: 'NUMBER', label: 'Nombre' },
  { value: 'DATE', label: 'Date' },
  { value: 'BOOLEAN', label: 'Oui/Non' },
  { value: 'SELECT', label: 'Liste de choix' },
];

interface Props {
  open: boolean;
  onClose: () => void;
  onChanged?: () => void; // called when a field has been added/changed/removed
}

const slugify = (s: string) =>
  s
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

export const FieldDefinitionsModal: React.FC<Props> = ({ open, onClose, onChanged }) => {
  const [fields, setFields] = useState<MarketingLeadField[]>([]);
  const [loading, setLoading] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newType, setNewType] = useState<MarketingLeadFieldType>('TEXT');
  const [newRequired, setNewRequired] = useState(false);
  const [adding, setAdding] = useState(false);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  const fetchFields = async () => {
    setLoading(true);
    try {
      const list = await listFields(false);
      setFields(list.sort((a, b) => a.sortOrder - b.sortOrder));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchFields();
  }, [open]);

  const handleAdd = async () => {
    setErrMsg(null);
    if (!newLabel.trim()) {
      setErrMsg('Saisissez un libellé');
      return;
    }
    const key = slugify(newLabel);
    if (!key) {
      setErrMsg('Libellé non valide');
      return;
    }
    setAdding(true);
    try {
      await createField({ label: newLabel.trim(), key, type: newType, required: newRequired });
      setNewLabel('');
      setNewType('TEXT');
      setNewRequired(false);
      await fetchFields();
      onChanged?.();
    } catch (e: any) {
      setErrMsg(e?.message ?? "Impossible d'ajouter le champ");
    } finally {
      setAdding(false);
    }
  };

  const handleToggleActive = async (f: MarketingLeadField) => {
    await updateField(f.id, { isActive: !f.isActive });
    await fetchFields();
    onChanged?.();
  };

  const [removeTarget, setRemoveTarget] = useState<MarketingLeadField | null>(null);
  const askRemove = (f: MarketingLeadField) => setRemoveTarget(f);
  const confirmRemove = async () => {
    if (!removeTarget) return;
    await removeField(removeTarget.id);
    setRemoveTarget(null);
    await fetchFields();
    onChanged?.();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Champs personnalisés</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Ces champs apparaissent dans le formulaire des leads, le mappage CSV et le sélecteur
              de variables des templates.
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Add new field */}
          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Ajouter un champ</h3>
            <div className="grid grid-cols-12 gap-3">
              <input
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder="Ex: Années d'expérience"
                className="col-span-6 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
              <select
                value={newType}
                onChange={(e) => setNewType(e.target.value as MarketingLeadFieldType)}
                className="col-span-3 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white"
              >
                {FIELD_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
              <label className="col-span-2 flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={newRequired}
                  onChange={(e) => setNewRequired(e.target.checked)}
                />
                Requis
              </label>
              <button
                onClick={handleAdd}
                disabled={adding || !newLabel.trim()}
                className="col-span-1 px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center justify-center"
                title="Ajouter"
              >
                {adding ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
              </button>
            </div>
            {newLabel && (
              <p className="text-xs text-gray-500 mt-2">
                Clé de variable : <code>customFields.{slugify(newLabel)}</code>
              </p>
            )}
            {errMsg && <p className="text-xs text-red-600 mt-1">{errMsg}</p>}
          </div>

          {/* List existing */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Champs existants</h3>
            {loading ? (
              <div className="text-center py-6 text-gray-400">
                <Loader2 className="inline animate-spin" />
              </div>
            ) : fields.length === 0 ? (
              <p className="text-sm text-gray-500 italic">Aucun champ pour le moment.</p>
            ) : (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold text-gray-700">Libellé</th>
                      <th className="text-left px-3 py-2 font-semibold text-gray-700">Clé</th>
                      <th className="text-left px-3 py-2 font-semibold text-gray-700">Type</th>
                      <th className="text-center px-3 py-2 font-semibold text-gray-700">Actif</th>
                      <th className="text-right px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {fields.map((f) => (
                      <tr key={f.id}>
                        <td className="px-3 py-2 text-gray-900">{f.label}</td>
                        <td className="px-3 py-2 text-gray-600 font-mono text-xs">{f.key}</td>
                        <td className="px-3 py-2 text-gray-600">
                          {FIELD_TYPES.find((t) => t.value === f.type)?.label}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <input
                            type="checkbox"
                            checked={f.isActive}
                            onChange={() => handleToggleActive(f)}
                          />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button
                            onClick={() => askRemove(f)}
                            className="p-1 text-red-500 hover:bg-red-50 rounded"
                            title="Désactiver"
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      <CuteConfirmModal
        isOpen={!!removeTarget}
        title="Désactiver ce champ"
        message="Le champ ne sera plus proposé dans le formulaire ni dans le mappage CSV. Les valeurs déjà saisies sur les leads existants restent intactes."
        itemName={removeTarget?.label}
        confirmLabel="Désactiver"
        variant="warning"
        onConfirm={confirmRemove}
        onCancel={() => setRemoveTarget(null)}
      />
    </div>
  );
};
